/**
 * sourcesStore — caches the merchant's linked integrations (Cost+ projects
 * + Shopify stores) so the SourcesPage AND the per-flow trigger picker
 * can read from a single hot list. Refreshes on focus and after OAuth
 * round-trip redirects (Sources reads `?shopify_connected=…` to know to
 * refresh instead of re-fetching on every open).
 */

import { create } from "zustand";
import { getApiClient } from "../../lib/apiClient";
import type { CostplusProject } from "../../types/api";
import type { ShopifyStore } from "../../lib/apiClient";

interface SourcesState {
  costplusProjects: CostplusProject[];
  shopifyStores: ShopifyStore[];
  loading: boolean;
  loadedAt: number;

  refresh: () => Promise<void>;
}

const STALE_MS = 30_000;

export const useSourcesStore = create<SourcesState>((set, get) => ({
  costplusProjects: [],
  shopifyStores: [],
  loading: false,
  loadedAt: 0,

  async refresh() {
    if (get().loading) return;
    set({ loading: true });
    try {
      const client = getApiClient();
      const [projects, stores] = await Promise.all([
        client.costplusProjects.list(),
        client.shopify.listStores(),
      ]);
      set({
        costplusProjects: projects,
        shopifyStores: stores,
        loading: false,
        loadedAt: Date.now(),
      });
    } catch {
      // Surface failures by leaving the lists as-is and dropping `loading`.
      // The cards have their own retry affordance.
      set({ loading: false });
    }
  },
}));

/** Returns true when the cache is older than the staleness threshold. */
export function sourcesIsStale(): boolean {
  const { loadedAt } = useSourcesStore.getState();
  return Date.now() - loadedAt > STALE_MS;
}
